// The Windows XP design system, in one place.
//
// Why this exists: thirteen app windows were built in a black-and-lime terminal palette, and Kris
// wants every one of them to look like 2001 Windows instead. Four sessions converting thirteen
// surfaces independently produces four different ideas of what XP looks like. This is the single
// definition they all import, so a group box in PAYDAY.exe is the same group box as in MISSION.exe.
//
// The reference is the casino restyle (WinXP/apps/Casino/ui.js) — the `xp__` chrome, the Luna
// push buttons, the group boxes and the sunken fields. Control recipes follow XP.css by botoxparty
// (MIT), rewritten here rather than imported so nothing new enters the bundle.
//
// USE IT TWO WAYS:
//   1. `import { xpCss } from 'lib/xp'` and drop `${xpCss}` into an existing styled block. That is
//      the low-friction path for the apps that are already one big styled.div — the class names
//      below start working immediately and the old palette can be deleted around them.
//   2. `import { GroupBox, Button, Field } from 'lib/xp'` for new markup.
//
// The lime is not gone, it is demoted. #ccff00 stays as the brand accent on the taskbar, the LED,
// the coin's own numbers and the logo — Internet Coin is still a lime coin. What it stops being is
// the background colour of every window.

import React from 'react';
import styled, { css } from 'styled-components';

// ── the palette ──────────────────────────────────────────────────────────────
// Real Luna values. These are the colours Windows XP actually shipped, not an impression of them.

export const XP = {
  // surfaces
  face: '#ece9d8', // the classic "3D Objects" beige — every dialog and client area
  faceAlt: '#f5f4ea', // a lighter card on top of face
  white: '#ffffff',
  field: '#ffffff', // the inside of a text box or list

  // the 3D edges that make XP look pressed and raised
  light: '#ffffff',
  shadow: '#aca899',
  dark: '#716f64',
  darkest: '#404040',

  // Luna blue
  blue: '#0054e3',
  blueLight: '#3f8cf3',
  blueDeep: '#003ec3',
  titleFrom: '#0058ee',
  titleTo: '#3a93ff',
  selection: '#316ac5',

  // Luna green (Start button, progress)
  green: '#3c9f3c',
  greenDeep: '#2f7d2f',

  // text
  ink: '#000000',
  inkSoft: '#3b3b3b',
  inkDim: '#6d6a5f',
  link: '#0000ee',

  // the brand, demoted to an accent
  lime: '#ccff00',
  limeDeep: '#7a9a00',

  // status
  red: '#c8102e',
  amber: '#ffd86b',
};

export const FONT = `'Tahoma', 'Noto Sans', 'Segoe UI', sans-serif`;
export const MONO = `'Lucida Console', 'Courier New', monospace`;

/** The two-tone bevel that does most of the work in a 2001 interface. */
export const raised = (a = XP.light, b = XP.shadow) => css`
  border: 1px solid ${b};
  border-top-color: ${a};
  border-left-color: ${a};
  box-shadow: inset 1px 1px 0 ${a}, inset -1px -1px 0 ${XP.dark};
`;

export const sunken = css`
  border: 1px solid ${XP.dark};
  border-right-color: ${XP.light};
  border-bottom-color: ${XP.light};
  box-shadow: inset 1px 1px 0 rgba(0, 0, 0, 0.25);
  background: ${XP.field};
`;

// ── the CSS block ────────────────────────────────────────────────────────────
// Drop `${xpCss}` into any styled block and every class below is available.

export const xpCss = css`
  font-family: ${FONT};
  font-size: 11px;
  color: ${XP.ink};
  background: ${XP.face};

  /* ---- program chrome ---------------------------------------------------- */
  .xp__menubar {
    display: flex;
    align-items: center;
    gap: 2px;
    padding: 2px 4px;
    background: linear-gradient(180deg, #fdfdfd 0%, #eeede4 46%, #e3e0d2 100%);
    border-bottom: 1px solid ${XP.shadow};
    user-select: none;
  }
  .xp__menubar button,
  .xp__menu {
    background: none;
    border: 1px solid transparent;
    font: 11px ${FONT};
    padding: 3px 8px;
    cursor: default;
    color: ${XP.ink};
  }
  .xp__menubar button:hover,
  .xp__menu:hover {
    border-color: ${XP.blueLight};
    background: #dceaff;
  }
  .xp__menubar__brand {
    margin-left: auto;
    padding-right: 6px;
    color: ${XP.inkDim};
    font-style: italic;
  }

  /* the IE6 address strip — the single strongest "this is 2001" cue we have */
  .xp__address {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 3px 6px;
    background: linear-gradient(180deg, #f6f5ee, #e8e6d9);
    border-bottom: 1px solid ${XP.shadow};
  }
  .xp__address__label {
    color: ${XP.inkDim};
  }
  .xp__address__field {
    flex: 1;
    min-width: 0;
    ${sunken};
    padding: 2px 6px;
    font-size: 11px;
    color: ${XP.inkSoft};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .xp__statusbar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 3px 8px;
    background: ${XP.face};
    border-top: 1px solid ${XP.light};
    box-shadow: inset 0 1px 0 ${XP.shadow};
    color: ${XP.inkDim};
  }
  .xp__statusbar > span {
    ${sunken};
    background: transparent;
    box-shadow: none;
    border-color: ${XP.shadow} ${XP.light} ${XP.light} ${XP.shadow};
    padding: 1px 8px;
  }
  .xp__statusbar > span:first-child {
    flex: 1;
  }

  /* ---- controls ---------------------------------------------------------- */
  .xp__btn {
    min-width: 75px;
    padding: 4px 12px;
    font: 11px ${FONT};
    color: ${XP.ink};
    background: linear-gradient(180deg, #fdfdfd 0%, #f1f0e8 48%, #e2e0d2 52%, #f3f2ea 100%);
    border: 1px solid ${XP.dark};
    border-radius: 3px;
    box-shadow: inset 0 0 0 1px ${XP.light};
    cursor: pointer;
  }
  .xp__btn:hover {
    border-color: ${XP.blue};
    box-shadow: inset 0 0 0 1px #fff, 0 0 4px rgba(63, 140, 243, 0.6);
  }
  .xp__btn:active {
    background: linear-gradient(180deg, #dedcd0, #eeede4);
    box-shadow: inset 1px 1px 2px rgba(0, 0, 0, 0.3);
  }
  .xp__btn:disabled {
    color: ${XP.shadow};
    text-shadow: 1px 1px 0 ${XP.light};
    cursor: default;
    box-shadow: none;
  }
  .xp__btn--default {
    border-color: ${XP.blueDeep};
    box-shadow: inset 0 0 0 1px #fff, inset 0 0 0 2px #8ab6ff;
  }

  .xp__field {
    ${sunken};
    padding: 3px 6px;
    font: 11px ${FONT};
    color: ${XP.ink};
    outline: none;
  }
  .xp__field:focus {
    border-color: ${XP.blue};
  }

  /* the labelled fieldset — the most XP thing there is */
  .xp__group {
    position: relative;
    border: 1px solid ${XP.shadow};
    border-top-color: ${XP.dark};
    background: transparent;
    margin: 16px 0 10px;
    padding: 14px 12px 12px;
  }
  .xp__group > legend,
  .xp__group__title {
    position: absolute;
    top: -7px;
    left: 8px;
    padding: 0 5px;
    background: ${XP.face};
    color: ${XP.inkSoft};
    font-size: 11px;
  }

  /* list / table */
  .xp__list {
    ${sunken};
    width: 100%;
    border-collapse: collapse;
    background: ${XP.field};
  }
  .xp__list th {
    text-align: left;
    font-weight: normal;
    padding: 3px 8px;
    background: linear-gradient(180deg, #fdfdfd, #eceadd);
    border-right: 1px solid ${XP.shadow};
    border-bottom: 1px solid ${XP.shadow};
    color: ${XP.inkSoft};
    position: sticky;
    top: 0;
  }
  .xp__list td {
    padding: 3px 8px;
    border-bottom: 1px solid #eeede4;
  }
  .xp__list tbody tr:hover td {
    background: #eef4ff;
  }
  .xp__list tbody tr.is-selected td {
    background: ${XP.selection};
    color: #fff;
  }

  /* the IE6 information bar — for notices that used to be lime-on-black warnings */
  .xp__infobar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 9px;
    background: linear-gradient(180deg, #fdf6c8, #f7e9a0);
    border: 1px solid #d4b93c;
    color: #3b2a00;
    margin-bottom: 10px;
  }
  .xp__infobar b {
    font-weight: bold;
  }

  /* a card on the beige, for grids of things */
  .xp__card {
    background: ${XP.faceAlt};
    ${raised()};
    padding: 10px 11px;
    text-align: left;
  }
  .xp__card:hover {
    background: #fbfaf3;
  }

  /* the coin's own numbers keep the lime, on a dark plate, like an LED readout */
  .xp__led {
    display: inline-block;
    ${sunken};
    background: #0b0f04;
    color: ${XP.lime};
    font-family: ${MONO};
    letter-spacing: 1px;
    padding: 2px 8px;
    text-shadow: 0 0 6px rgba(204, 255, 0, 0.55);
  }

  .xp__hr {
    height: 2px;
    border: 0;
    background: ${XP.shadow};
    border-bottom: 1px solid ${XP.light};
    margin: 10px 0;
  }
  a {
    color: ${XP.link};
  }
  h1, h2, h3 {
    font-weight: bold;
    font-size: 13px;
    margin: 0 0 6px;
  }
`;

// ── components, for new markup ───────────────────────────────────────────────

export const Program = styled.div`
  ${xpCss};
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .xp__client {
    flex: 1;
    overflow: auto;
    padding: 12px 14px;
    background: ${XP.face};
  }
`;

export function GroupBox({ title, children, className = '', ...rest }) {
  return (
    <div className={`xp__group ${className}`} {...rest}>
      {title ? <span className="xp__group__title">{title}</span> : null}
      {children}
    </div>
  );
}

export function Button({ children, primary, className = '', ...rest }) {
  return (
    <button type="button" className={`xp__btn${primary ? ' xp__btn--default' : ''} ${className}`} {...rest}>
      {children}
    </button>
  );
}

export function Field({ className = '', ...rest }) {
  return <input className={`xp__field ${className}`} {...rest} />;
}

export function InfoBar({ children, className = '' }) {
  return <div className={`xp__infobar ${className}`}>{children}</div>;
}

/** The coin's numbers, kept lime on a dark plate so the brand survives the beige. */
export function Led({ children, className = '' }) {
  return <span className={`xp__led ${className}`}>{children}</span>;
}

/** Address strip. `path` should read like a 2001 URL, e.g. payday.internetcoin.vip/ledger.htm */
export function AddressBar({ path }) {
  return (
    <div className="xp__address">
      <span className="xp__address__label">Address</span>
      <div className="xp__address__field">http://{path}</div>
    </div>
  );
}

export function StatusBar({ children }) {
  return <div className="xp__statusbar">{children}</div>;
}

export default XP;

// ── the 2001 web page layer ──────────────────────────────────────────────────
// Kris 2026-09-13 (on the beige conversions): "They're all gray, boring, and the text is hard to
// read in the boxes… I want the tabs to be colorful, Windows XP nostalgic, 2001 internet, but
// colorful and nice in design. Same like you designed the casino."
//
// So a program window is an XP frame (menubar, address strip, status bar — above) around a
// PERIOD WEB PAGE: a gradient masthead with a gold serif title and a red drop shadow, a marquee,
// cream bevelled panels with coloured edges, gold "BUY NOW!" bevel buttons, Impact stickers, an
// odometer visitor counter, a rainbow rule and a 2001 table with coloured cell gaps. The casino's
// DNA (Casino/ui.js) generalised: every page gets its own hue via a `web--*` modifier, the parts
// stay the same. Readability rule: dark ink on light, light ink on dark, never ink on ink.

export const WEB = {
  navy: '#0a246a',
  navyLight: '#2c56c9',
  blue: '#3159d8',
  sky: '#a6caf0',
  cream: '#fffbe6',
  paper: '#fff7d6',
  gold: '#ffd86b',
  goldDeep: '#c9a227',
  goldInk: '#4a3600',
  red: '#c8102e',
  orange: '#ff8c00',
  purple: '#5b1fa8',
  purpleLight: '#9b5de5',
  felt: '#0b5d33',
  feltLight: '#178a4d',
  teal: '#0e7c7b',
  tealLight: '#37b3b1',
  skyBlue: '#1d5fd6',
  skyLight: '#5fa8ff',
  brown: '#5a3413',
  brownLight: '#8a5a2b',
  ink: '#1d1d1d',
  lime: '#ccff00',
};
export const SERIF = `'Times New Roman', Times, Georgia, serif`;
export const STICKER = `Impact, 'Arial Black', 'Helvetica Neue', sans-serif`;

export const webCss = css`
  .web {
    background: ${WEB.cream}
      repeating-linear-gradient(135deg, rgba(0, 0, 0, 0.025) 0 6px, transparent 6px 14px);
    color: ${WEB.ink};
    font: 11px/1.45 ${FONT};
    min-height: 100%;
  }
  .web__body {
    padding: 12px 14px 14px;
  }

  /* masthead: the page's own colour, gold serif title, red drop shadow */
  .web__mast {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px 14px;
    padding: 12px 16px;
    background: linear-gradient(180deg, ${WEB.navyLight} 0%, ${WEB.navy} 55%, #061640 100%);
    border-bottom: 3px solid ${WEB.gold};
    box-shadow: inset 0 -1px 0 ${WEB.goldDeep};
  }
  .web--orange .web__mast {
    background: linear-gradient(180deg, #ffb347 0%, ${WEB.orange} 50%, #c35a00 100%);
  }
  .web--purple .web__mast {
  /* lime — the coin's own page. Dark ink on lime, never white (house rule). */
  .web--lime .web__mast {
    background: linear-gradient(180deg, #eaff8c 0%, ${WEB.lime} 52%, #8fb800 100%);
    border-bottom: 3px solid ${WEB.goldDeep};
  }
  .web--lime .web__mast h1,
  .web--lime .web__mast__kicker {
    color: #1d2a00;
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.5);
  }
  .web__panel--lime {
    border-color: #f4ffc2 #7a9a00 #7a9a00 #f4ffc2;
  }
  .web__panel--lime .web__panel__t {
    background: ${WEB.lime};
    color: ${WEB.ink};
  }
    background: radial-gradient(circle at 20% 30%, rgba(255, 255, 255, 0.18) 0 2px, transparent 3px),
      radial-gradient(circle at 70% 60%, rgba(255, 255, 255, 0.14) 0 1.5px, transparent 2.5px),
      radial-gradient(circle at 45% 20%, rgba(255, 255, 255, 0.12) 0 1.5px, transparent 2.5px),
      linear-gradient(180deg, ${WEB.purpleLight} 0%, ${WEB.purple} 55%, #2a0b52 100%);
  }
  .web--felt .web__mast {
    background: linear-gradient(180deg, ${WEB.feltLight} 0%, ${WEB.felt} 55%, #063b20 100%);
  }
  .web--red .web__mast {
    background: linear-gradient(180deg, #ff5a6e 0%, ${WEB.red} 55%, #7a0a1c 100%);
  }
  /* teal — THE ZONE (the gaming zone was always aqua) */
  .web--teal .web__mast {
    background: linear-gradient(180deg, ${WEB.tealLight} 0%, ${WEB.teal} 55%, #064a49 100%);
  }
  /* sky — MSFT.exe (a brighter Microsoft blue than Verify's navy) */
  .web--sky .web__mast {
    background: linear-gradient(180deg, ${WEB.skyLight} 0%, ${WEB.skyBlue} 55%, #0b2f7a 100%);
  }
  /* brown — BASEMENT.exe (underground, wood and rust) */
  .web--brown .web__mast {
    background: linear-gradient(180deg, ${WEB.brownLight} 0%, ${WEB.brown} 55%, #2e1a08 100%);
  }
  .web--teal .web__mast__kicker,
  .web--brown .web__mast__kicker {
    color: #fff3dc;
  }
  .web__mast img {
    width: 58px;
    height: 58px;
    filter: drop-shadow(0 0 10px rgba(204, 255, 0, 0.55)) drop-shadow(2px 3px 0 rgba(0, 0, 0, 0.35));
  }
  .web__mast__kicker {
    font: 10px ${FONT};
    letter-spacing: 3px;
    text-transform: uppercase;
    color: ${WEB.sky};
    margin-bottom: 3px;
  }
  .web--orange .web__mast__kicker,
  .web--red .web__mast__kicker {
    color: #fff3dc;
  }
  .web__mast h1 {
    margin: 0;
    font: bold 26px/1.05 ${SERIF};
    letter-spacing: 1px;
    color: ${WEB.gold};
    text-shadow: 2px 2px 0 ${WEB.red}, 3px 3px 0 rgba(0, 0, 0, 0.55);
  }
  .web--orange .web__mast h1 {
    text-shadow: 2px 2px 0 ${WEB.navy}, 3px 3px 0 rgba(0, 0, 0, 0.55);
  }
  .web__mast__right {
    margin-left: auto;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }

  /* the marquee under the masthead: black strip, gold Lucida Console, scrolls forever */
  .web__marquee {
    background: #000;
    color: ${WEB.gold};
    border-top: 1px solid ${WEB.goldDeep};
    border-bottom: 1px solid ${WEB.goldDeep};
    font: 11px/22px ${MONO};
    height: 22px;
    overflow: hidden;
    white-space: nowrap;
    position: relative;
  }
  .web__marquee span {
    position: absolute;
    left: 0;
    padding-left: 100%;
    animation: web-marquee 24s linear infinite;
  }
  @keyframes web-marquee {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-100%);
    }
  }

  /* cream bevelled panels with a coloured EDGE, not a coloured fill: the panel body stays cream/white, so
     text inside it is dark ink. Only the web__panel__t title bar is the page colour (light text there).
     (Never backtick-quote a class name inside this block — it is a template literal and a stray
     backtick closes it. That broke the build once already.)
     Writing white text into a panel body is the trap that recreates the ink-on-ink bug. */
  .web__panel {
    background: ${WEB.paper};
    border: 2px solid;
    border-color: #fff ${WEB.goldDeep} ${WEB.goldDeep} #fff;
    box-shadow: 2px 2px 0 rgba(0, 0, 0, 0.28);
    padding: 10px 12px;
    margin-bottom: 12px;
  }
  .web__panel--navy {
    border-color: ${WEB.sky} ${WEB.navy} ${WEB.navy} ${WEB.sky};
  }
  .web__panel--orange {
    border-color: #ffd9a8 #b85400 #b85400 #ffd9a8;
  }
  .web__panel--purple {
    border-color: #d9c2ff ${WEB.purple} ${WEB.purple} #d9c2ff;
  }
  .web__panel--felt {
    border-color: #b8f0cf ${WEB.felt} ${WEB.felt} #b8f0cf;
  }
  .web__panel--white {
    background: #fff;
  }
  .web__panel--teal {
    border-color: #b3ecea ${WEB.teal} ${WEB.teal} #b3ecea;
  }
  .web__panel--sky {
    border-color: #cfe3ff ${WEB.skyBlue} ${WEB.skyBlue} #cfe3ff;
  }
  .web__panel--brown {
    border-color: #e8d2b8 ${WEB.brown} ${WEB.brown} #e8d2b8;
  }
  .web__panel--red {
    border-color: #ffc4cc ${WEB.red} ${WEB.red} #ffc4cc;
  }
  .web__panel--blue {
    border-color: #c9d8ff ${WEB.blue} ${WEB.blue} #c9d8ff;
  }
  .web__panel__t {
    margin: -10px -12px 10px;
    padding: 4px 10px;
    background: ${WEB.navy};
    color: ${WEB.gold};
    font: bold 11px ${FONT};
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  .web__panel--orange .web__panel__t {
    background: ${WEB.orange};
    color: #fff;
    text-shadow: 1px 1px 0 #7a3600;
  }
  .web__panel--purple .web__panel__t {
    background: ${WEB.purple};
  }
  .web__panel--felt .web__panel__t {
    background: ${WEB.felt};
  }
  .web__panel--teal .web__panel__t {
    background: ${WEB.teal};
  }
  .web__panel--sky .web__panel__t {
    background: ${WEB.skyBlue};
  }
  .web__panel--brown .web__panel__t {
    background: ${WEB.brown};
  }
  .web__panel--red .web__panel__t {
    background: ${WEB.red};
    color: #fff;
  }
  .web__panel--blue .web__panel__t {
    background: ${WEB.blue};
    color: #fff;
  }
  .web__cols {
    display: flex;
    gap: 12px;
    align-items: stretch;
  }
  .web__cols > * {
    flex: 1;
    min-width: 0;
  }

  /* the 2001 bevel buttons — gold is the "PLAY NOW!" of every gambling site, red and blue for the rest */
  .web__btn {
    display: inline-block;
    padding: 7px 18px;
    font: bold 14px/1 ${SERIF};
    letter-spacing: 1px;
    text-transform: uppercase;
    text-decoration: none;
    color: ${WEB.goldInk};
    background: linear-gradient(180deg, #fff2a8 0%, ${WEB.gold} 45%, #e0b23c 55%, #f3cf6a 100%);
    border: 2px solid;
    border-color: #fff8d6 #8a6d1c #8a6d1c #fff8d6;
    box-shadow: 2px 2px 0 #000, inset 0 0 0 1px #fff;
    text-shadow: 0 1px 0 #fff;
    cursor: pointer;
  }
  .web__btn:hover {
    filter: brightness(1.06);
  }
  .web__btn:active {
    transform: translate(1px, 1px);
    box-shadow: 1px 1px 0 #000, inset 0 0 0 1px #fff;
  }
  .web__btn--red {
    color: #fff;
    background: linear-gradient(180deg, #ff7b8b 0%, ${WEB.red} 50%, #8f0a1e 100%);
    border-color: #ffc4cc #5a0612 #5a0612 #ffc4cc;
    text-shadow: 1px 1px 0 #5a0612;
    box-shadow: 2px 2px 0 #000, inset 0 0 0 1px rgba(255, 255, 255, 0.5);
  }
  .web__btn--blue {
    color: #fff;
    background: linear-gradient(180deg, #7fa3ff 0%, ${WEB.blue} 50%, #17307f 100%);
    border-color: #c9d8ff #0c1a4f #0c1a4f #c9d8ff;
    text-shadow: 1px 1px 0 #0c1a4f;
    box-shadow: 2px 2px 0 #000, inset 0 0 0 1px rgba(255, 255, 255, 0.5);
  }
  .web__btn--big {
    font-size: 18px;
    padding: 10px 26px;
  }
  .web__btn--off {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
  }

  /* Impact stickers: NEW!, HOT!, LIVE */
  .web__sticker {
    display: inline-block;
    padding: 2px 8px 1px;
    font: 13px/1.2 ${STICKER};
    letter-spacing: 1px;
    color: #fff;
    background: ${WEB.red};
    transform: rotate(-6deg);
    box-shadow: 1px 1px 0 #000, 2px 2px 0 rgba(0, 0, 0, 0.3);
    text-shadow: 1px 1px 0 rgba(0, 0, 0, 0.5);
  }
  .web__sticker--orange {
    background: ${WEB.orange};
  }
  .web__sticker--blue {
    background: ${WEB.blue};
  }
  .web__sticker--gold {
    background: ${WEB.gold};
    color: ${WEB.goldInk};
    text-shadow: none;
  }
  .web__sticker--blink {
    animation: web-blink 1s steps(2, start) infinite;
  }
  @keyframes web-blink {
    to {
      visibility: hidden;
    }
  }

  /* the odometer visitor counter — lime digits on black, each in its own cell */
  .web__counter {
    display: inline-flex;
    gap: 2px;
    padding: 3px 4px;
    background: #000;
    border: 2px solid;
    border-color: #555 #ddd #ddd #555;
    vertical-align: middle;
  }
  .web__counter i {
    display: inline-block;
    width: 13px;
    height: 20px;
    background: linear-gradient(180deg, #14200a, #0b0f04);
    color: ${WEB.lime};
    font: bold 15px/20px ${MONO};
    font-style: normal;
    text-align: center;
    border: 1px solid #2a3a12;
    text-shadow: 0 0 5px rgba(204, 255, 0, 0.6);
  }

  /* the readable LED plate: lime on dark navy, generous padding, wraps long values */
  .web__led {
    display: inline-block;
    padding: 5px 10px;
    background: #071433;
    color: ${WEB.lime};
    font: bold 12px/1.4 ${MONO};
    letter-spacing: 1px;
    border: 2px solid;
    border-color: #0c1a4f #4d6bb5 #4d6bb5 #0c1a4f;
    text-shadow: 0 0 6px rgba(204, 255, 0, 0.55);
    word-break: break-all;
    max-width: 100%;
  }
  .web__led--big {
    font-size: 18px;
    padding: 6px 14px;
  }

  /* the rainbow rule that sat under every 2001 heading */
  .web__hr {
    height: 4px;
    border: 0;
    margin: 10px 0;
    background: linear-gradient(90deg, ${WEB.red}, ${WEB.orange}, ${WEB.gold}, #1e8b3a, ${WEB.blue}, ${WEB.purple});
  }

  /* a table with coloured cell gaps, navy header row, striped rows */
  .web__table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 2px;
    background: ${WEB.navy};
    font-size: 11px;
  }
  .web--purple .web__table {
    background: ${WEB.purple};
  }
  .web--orange .web__table {
    background: #b85400;
  }
  .web--teal .web__table {
    background: ${WEB.teal};
  }
  .web--sky .web__table {
    background: ${WEB.skyBlue};
  }
  .web--brown .web__table {
    background: ${WEB.brown};
  }
  .web__table th {
    text-align: left;
    padding: 4px 8px;
    color: ${WEB.gold};
    font-weight: bold;
    background: transparent;
  }
  .web__table td {
    padding: 4px 8px;
    background: #fff;
    color: ${WEB.ink};
  }
  .web__table tr:nth-child(even) td {
    background: #eef3ff;
  }
  .web--purple .web__table tr:nth-child(even) td {
    background: #f3ecff;
  }
  .web__table tr.is-next td {
    background: #fff4c4;
    font-weight: bold;
  }
  .web__table tr.is-done td {
    color: #1e8b3a;
  }

  /* steps with a gold numbered medallion */
  .web__step {
    display: flex;
    gap: 10px;
    align-items: flex-start;
    margin: 8px 0;
  }
  .web__step__n {
    flex: 0 0 26px;
    width: 26px;
    height: 26px;
    border-radius: 50%;
    background: radial-gradient(circle at 35% 30%, #fff2a8, ${WEB.gold} 45%, ${WEB.goldDeep} 100%);
    color: ${WEB.goldInk};
    font: bold 13px/26px ${SERIF};
    text-align: center;
    border: 1px solid #8a6d1c;
    box-shadow: 1px 1px 0 rgba(0, 0, 0, 0.35);
  }
  .web__step b {
    display: block;
    margin-bottom: 2px;
  }

  .web__note {
    background: #fffbcc;
    border: 1px dashed ${WEB.goldDeep};
    padding: 6px 9px;
    font-size: 10.5px;
    color: #4a3600;
    margin-top: 10px;
  }
  .web__foot {
    text-align: center;
    font-size: 10px;
    color: #6a6350;
    padding: 6px 0 2px;
  }
  .web a {
    color: #0000ee;
  }
  .web a:visited {
    color: #551a8b;
  }
  .web__center {
    text-align: center;
  }
  .web__row {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
  }
`;

/** Digits in odometer cells, zero-padded. */
export function Counter({ value, digits = 7 }) {
  const s = String(Math.max(0, Math.floor(Number(value) || 0))).padStart(digits, '0');
  return (
    <span className="web__counter" aria-label={`${s}`}>
      {[...s].map((d, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <i key={i}>{d}</i>
      ))}
    </span>
  );
}

export function Marquee({ children }) {
  return (
    <div className="web__marquee">
      <span>{children}</span>
    </div>
  );
}

/** The masthead: logo, kicker, gold serif title, optional right slot (stickers, counters). */
export function Mast({ logo, kicker, title, right }) {
  return (
    <div className="web__mast">
      {logo ? <img src={logo} alt="" /> : null}
      <div>
        {kicker ? <div className="web__mast__kicker">{kicker}</div> : null}
        <h1>{title}</h1>
      </div>
      {right ? <div className="web__mast__right">{right}</div> : null}
    </div>
  );
}
